import React, { useState, useEffect } from 'react';
import { Button, Input, Select } from '..';
import {
  FiCheck,
  FiMonitor,
  FiDollarSign,
  FiCreditCard,
  FiHash,
  FiCalendar,
  FiInfo,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { BANK_OPTIONS } from '../../constants/banks';
import Api from '../../Api';
import '../CompanyForm/style.css';



const ACCOUNT_TYPES = [
  { value: 'checking', label: 'Conta Corrente' },
  { value: 'cash', label: 'Conta Caixinha' },
  { value: 'other', label: 'Outras Contas' },
];

const MODALITY_OPTIONS = [
  { value: 'pj', label: 'Pessoa Jurídica' },
  { value: 'pf', label: 'Pessoa Física' },
];

export default function BankAccountForm({ companyId, initialData, onSuccess, onCancel, companies = [] }) {
  const isEdit = !!initialData;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    type: 'checking',
    bank: '',
    agency: '',
    account_number: '',
    modality: 'pj',
    integration_type: 'manual_import',
    initial_balance_date: '',
    initial_balance: '',
    company_ids: companyId ? [companyId] : [],
  });

  useEffect(() => {
    if (initialData) {
      const formattedBalance = initialData.initial_balance 
        ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(initialData.initial_balance)
        : '';
      
      let initialCompanyIds = [];
      if (initialData.companies && Array.isArray(initialData.companies)) {
        initialCompanyIds = initialData.companies.map(c => c.id);
      } else if (initialData.company_id) {
        initialCompanyIds = [initialData.company_id];
      } else if (companyId) {
        initialCompanyIds = [companyId];
      }

      setFormData({
        ...initialData,
        initial_balance: formattedBalance,
        company_ids: initialCompanyIds,
      });
    }
  }, [initialData, companyId]);

  const handleChange = (e) => {
    const { name, value, id } = e.target;
    // Handle both standard inputs (name) and Select components (id)
    const fieldName = name || id;
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  function parseCurrency(value) {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    return parseFloat(value.replace(/[^\d,]/g, '').replace(',', '.'));
  }

  const validateForm = () => {
    const messages = [];

    if (!formData.name.trim()) {
      messages.push('Informe o nome da conta.');
    }

    if (!formData.company_ids || formData.company_ids.length === 0) {
      messages.push('Selecione pelo menos uma empresa.');
    }
    
    if (formData.type === 'checking' && !formData.bank) {
      messages.push('Selecione o banco.');
    }

    // Validation for both integration types now
    if (formData.integration_type === 'no_extract') {
      if (!formData.initial_balance_date) {
        messages.push('Informe a data do saldo inicial.');
      }
      if (!formData.initial_balance && formData.initial_balance !== 0) {
        messages.push('Informe o saldo inicial.');
      }
    }

    if (messages.length > 0) {
      messages.forEach(msg => toast.error(msg));
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      const cleanData = {
        ...formData,
        initial_balance: parseCurrency(formData.initial_balance),
        initial_balance_date: formData.initial_balance_date || new Date().toISOString().split('T')[0],
      };

      if (isEdit) {
        cleanData['balance'] = cleanData.initial_balance;
        
        // Determine a valid companyId for the URL context
        let targetCompanyId = companyId;
        if (targetCompanyId === 'all' || !targetCompanyId) {
            if (formData.company_ids && formData.company_ids.length > 0) {
                targetCompanyId = formData.company_ids[0];
            } else {
                // Fallback to first available company if no specific selected (should be caught by validation)
                 if (companies.length > 0) targetCompanyId = companies[0].id;
            }
        }
        
        await Api.updateAccount(targetCompanyId, initialData.id, cleanData);
        toast.success('Conta bancária atualizada com sucesso!');
      } else {
        const payload = { 
          ...cleanData, 
          balance: cleanData.initial_balance 
        };
        
        // Determine a valid companyId for the URL context
        let targetCompanyId = companyId;
        if (targetCompanyId === 'all' || !targetCompanyId) {
            if (formData.company_ids && formData.company_ids.length > 0) {
                targetCompanyId = formData.company_ids[0];
            } else {
                 if (companies.length > 0) targetCompanyId = companies[0].id;
            }
        }
        
        await Api.createAccount(targetCompanyId, payload);
        toast.success('Conta bancária criada com sucesso!');
      }
      onSuccess();
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        'Ocorreu um erro ao salvar. Tente novamente.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = () => {
    switch (formData.type) {
      case 'checking': return FiMonitor;
      case 'cash': return FiDollarSign;
      default: return FiCreditCard;
    }
  };

  return (
    <form className="company-form-component" onSubmit={handleSubmit}>
      <div className="form-body">
        {error && <div className="form-error-banner">{error}</div>}

        <div className="section-title">Dados da Conta</div>
        
        <div className="form-grid" style={{ gridTemplateColumns: '1fr', marginTop: '10px' }}>
          <Select
             id="company_ids"
             label="Empresas Vinculadas *"
             value={formData.company_ids}
             onChange={handleChange}
             options={companies.map(c => ({ value: c.id, label: c.trade_name || c.corporate_name }))}
             placeholder="SELECIONE AS EMPRESAS..."
             searchable
             multiple
          />
        </div>

        <div className="form-grid" style={{ gridTemplateColumns: '1fr 2fr', marginTop: '10px' }}>
          <Select
            id="type"
            label="Tipo de Conta *"
            value={formData.type}
            onChange={handleChange}
            options={ACCOUNT_TYPES}
            icon={getIcon()}
            searchable
          />
          
          <Input
            name="name"
            label="Nome da Conta *"
            value={formData.name}
            onChange={handleChange}
            placeholder="Ex: Banco do Brasil"
          />
        </div>

        {formData.type !== 'cash' && (
          <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr', marginTop: '10px' }}>
            <Select
              id="bank"
              label="Banco *"
              value={formData.bank}
              onChange={handleChange}
              options={BANK_OPTIONS}
              placeholder="Selecione o banco..."
              searchable
            />
            
            <Select
              id="modality"
              label="Modalidade"
              value={formData.modality}
              onChange={handleChange}
              options={MODALITY_OPTIONS}
              searchable
            />
          </div>
        )}

        {formData.type !== 'cash' && (
          <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: '10px' }}>
            <Input
              name="agency"
              label="Agência"
              value={formData.agency}
              onChange={handleChange}
              placeholder="0000-0"
              mask="agency"
              icon={FiHash}
            />
            <Input
              name="account_number"
              label="Conta"
              value={formData.account_number}
              onChange={handleChange}
              placeholder="00000-0"
              mask="account"
              icon={FiHash}
            />
          </div>
        )}

        <div className="section-title" style={{ marginTop: '2rem' }}>Defina a forma de cadastro *</div>

        <div className="integration-section">
          <div className="integration-toggle-group">
            <button
              type="button"
              className={`integration-toggle-btn ${formData.integration_type === 'manual_import' ? 'active' : ''}`}
              onClick={() => handleChange({ target: { name: 'integration_type', value: 'manual_import' } })}
            >
              Importar extrato manualmente
            </button>
            <button
              type="button"
              className={`integration-toggle-btn ${formData.integration_type === 'no_extract' ? 'active' : ''}`}
              onClick={() => handleChange({ target: { name: 'integration_type', value: 'no_extract' } })}
            >
              Cadastrar sem extrato
            </button>
          </div>

          <div className="integration-info-box">
            <div className="integration-info-icon">
              <FiInfo size={24} />
            </div>
            <div className="integration-info-content">
              {formData.integration_type === 'manual_import' ? (
                <>
                  <h4>Importe o extrato do seu banco quando quiser.</h4>
                  <ul>
                    <li>Ideal para bancos sem integração automática.</li>
                    <li>Importe arquivos .OFX baixado do seu internet banking.</li>
                    <li>Concilie os lançamentos com o financeiro.</li>
                    <li>Os lançamentos podem ser importados até o dia de ontem.</li>
                    <li>O formato do arquivo deve ser .OFX (Money 2000 v.102).</li>
                  </ul>
                </>
              ) : (
                <>
                  <h4>Cadastre sua conta agora e integre ou importe depois.</h4>
                  <ul>
                    <li>Comece a usar a conta no sistema imediatamente.</li>
                    <li>Você pode integrar ou importar extratos depois.</li>
                    <li className="info-highlight-red">O cadastro da conta não trará a sua movimentação.</li>
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: '10px' }}>
          <Input
            name="initial_balance_date"
            label="Início dos lançamentos *"
            type="date"
            value={formData.initial_balance_date}
            onChange={handleChange}
            icon={FiCalendar}
          />
          <Input
            name="initial_balance"
            label="Saldo final da conta *"
            value={formData.initial_balance}
            onChange={handleChange}
            placeholder="R$ 0,00"
            mask="currency"
            icon={FiDollarSign}
          />
        </div>
      </div>

      <div className="form-actions">
        <Button 
          type="button" 
          className="cancel-button" 
          onClick={onCancel}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button 
          type="submit" 
          disabled={loading}
          icon={FiCheck}
        >
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}
